<template>
    <div class="space-y-6">
        <div class="flex justify-between space-x-4">
            <UiPeriodSwitcher
                :from="from"
                :to="to"
                :period="viewMode"
                @changed="changeDates"
            >
                <span v-if="viewMode === 'month'">{{ $filters.format(from, 'MMMM YYYY') }}</span>
                <span v-else-if="viewMode === 'week'">
                    {{ $filters.format(from, 'DD') }} au
                    {{ $filters.format(to, 'DD MMMM YYYY') }}
                </span>
                <span v-else-if="viewMode === 'day'">
                    {{ $filters.format(from, 'DD MMMM YYYY') }}
                </span>
            </UiPeriodSwitcher>
            <div class="flex space-x-2">
                <div class="btn-group">
                    <button
                        :disabled="viewMode === 'day'"
                        class="btn"
                        @click="setDayMode"
                    >
                        Jour
                    </button>
                    <button
                        :disabled="viewMode === 'week'"
                        class="btn"
                        @click="setWeekMode"
                    >
                        Semaine
                    </button>
                    <button
                        :disabled="viewMode === 'month'"
                        class="btn"
                        @click="setMonthMode"
                    >
                        Mois
                    </button>
                </div>
                <UiSelect
                    v-if="accessStaff"
                    v-model="filters.classroom_uuids"
                    name="classroom"
                    :options="classroomOptions"
                    multiple
                    placeholder="Filtrer par section"
                />
                <UiPrintButton id="staff-calendar" />
                <button
                    v-if="accessStaff"
                    class="btn btn-icon"
                    :disabled="loadingStates.excel"
                    :class="{ loading: loadingStates.excel }"
                    @click="downloadExcel"
                >
                    <ArrowDownTrayIconMini class="size-5" />
                </button>
            </div>
        </div>
        <div
            id="staff-calendar"
            class="bg-white"
        >
            <table
                v-if="viewMode === 'month'"
                aria-describedby="Calendrier"
                class="w-full table-fixed bordered"
            >
                <thead>
                    <tr class="bg-white">
                        <th
                            scope="col"
                            class="px-1 py-1"
                        />
                        <th
                            v-for="day in days"
                            :key="day"
                            scope="col"
                            class="sticky top-0 z-0 w-8 px-0 py-0 text-sm text-center print:w-6"
                            :class="{
                                'bg-slate-300': isWeekend(day),
                                'bg-white': !isWeekend(day) && !today.isSame(day, 'day'),
                                'bg-primary-500/70': today.isSame(day, 'day')
                            }"
                        >
                            {{ $filters.format(day, 'D') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="staff in staffWithAttendances"
                        :key="staff.uuid"
                        class="hover:bg-slate-200"
                    >
                        <td>
                            <div
                                class="flex items-center ml-1 space-x-2 cursor-pointer"
                                @click="goTo(`/staff/${staff.uuid}`)"
                            >
                                <img
                                    class="w-6 h-6 rounded-full shadow"
                                    :src="staff.avatar"
                                    alt="staff avatar"
                                >
                                <span class="text-xs">{{ staff.name }}</span>
                            </div>
                        </td>
                        <td
                            v-for="day in days"
                            :key="day"
                            class="relative w-8 h-8 p-0 print:w-6 print:h-6"
                        >
                            <svg
                                height="100%"
                                width="100%"
                                viewBox="0 0 100 100"
                                preserveAspectRatio="none"
                            >
                                <g v-if="dayOfStaff(day, staff)">
                                    <polygon
                                        v-if="[1, 3, 5, 7].includes(dayOfStaff(day, staff).type) && parseInt(dayOfStaff(day, staff).should_start_at.slice(11, 13)) < 12"
                                        points="0,0 100,0 0,100"
                                        class="cursor-pointer fill-current half hover:text-primary-300"
                                        :class="['text-' + dayOfStaff(day, staff).color.color]"
                                        @click="edit(dayOfStaff(day, staff))"
                                    />
                                    <polygon
                                        v-else-if="[1, 3, 5, 7].includes(dayOfStaff(day, staff).type) && parseInt(dayOfStaff(day, staff).should_start_at.slice(11, 13)) >= 12"
                                        points="100,100 100,0 0,100"
                                        class="cursor-pointer fill-current half hover:text-primary-300"
                                        :class="['text-' + dayOfStaff(day, staff).color.color]"
                                        @click="edit(dayOfStaff(day, staff))"
                                    />
                                    <polygon
                                        v-else
                                        points="0,0 100,0 100,100 0,100"
                                        class="cursor-pointer fill-current half hover:text-primary-300"
                                        :class="['text-' + dayOfStaff(day, staff).color.color]"
                                        @click="edit(dayOfStaff(day, staff))"
                                    />
                                </g>
                                <g v-else>
                                    <polygon
                                        points="0,0 100,0 100,100 0,100"
                                        class="cursor-pointer fill-current half hover:text-primary-300"
                                        :class="{
                                            'text-slate-300': isWeekend(day),
                                            'text-white': !isWeekend(day)
                                        }"
                                        @click="create(day, staff)"
                                    />
                                </g>
                            </svg>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table
                v-if="viewMode === 'week'"
                class="w-full text-xs table-fixed bordered"
                aria-describedby="table-title"
            >
                <thead>
                    <tr>
                        <th
                            scope="col"
                            class="w-48 py-2"
                        />
                        <th
                            v-for="date in days"
                            :key="date"
                            scope="col"
                            colspan="2"
                            class="py-2 text-sm text-gray-900 capitalize bg-slate-300"
                        >
                            {{ $filters.format(date, 'dddd DD MMMM') }}
                        </th>
                    </tr>
                    <tr>
                        <th
                            scope="col"
                            class="font-semibold text-center text-gray-800 bg-slate-200"
                        />
                        <template
                            v-for="date in days"
                            :key="date"
                        >
                            <td class="py-2 font-semibold text-center text-gray-800 bg-slate-200">
                                Matin
                            </td>
                            <td class="py-2 font-semibold text-center text-gray-800 bg-slate-200">
                                Après-Midi
                            </td>
                        </template>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="staff in staffWithAttendances"
                        :key="staff.uuid"
                    >
                        <td>
                            <div class="flex items-center ml-1 space-x-2">
                                <img
                                    class="w-6 h-6 rounded-full shadow"
                                    :src="staff.avatar"
                                    alt="staff avatar"
                                >
                                <span class="text-xs">{{ staff.name }}</span>
                            </div>
                            <div class="flex items-center justify-between">
                                <div
                                    v-if="staff.total_hours"
                                    class="pr-1 text-xs text-right text-gray-500"
                                >
                                    Théorique: {{ staff.total_hours.toString().replace('.', ',') }}h
                                </div>
                                <div
                                    v-if="staff.real_total_hours && canSeeRealHoursForStaff(staff)"
                                    class="pr-1 text-xs text-right text-gray-500"
                                >
                                    Réel: {{ staff.real_total_hours.toString().replace('.', ',') }}h
                                </div>
                            </div>
                        </td>
                        <td
                            v-for="day in days"
                            :key="day"
                            class="relative h-8 p-0 print:h-6"
                            colspan="2"
                        >
                            <div
                                v-if="dayOfStaff(day, staff)"
                                class="absolute inset-y-0 flex flex-col items-center justify-center text-white cursor-pointer hover:bg-blue-400"
                                :class="[
                                    'bg-' + dayOfStaff(day, staff).color.color,
                                    [1, 3, 5, 7].includes(dayOfStaff(day, staff).type) ? 'w-1/2' : 'w-full',
                                    isAm(dayOfStaff(day, staff)) ? '' : 'right-0'
                                ]"
                                @click="edit(dayOfStaff(day, staff))"
                            >
                                <div
                                    v-if="canSeeRealHoursForStaff(staff) && (dayOfStaff(day, staff).start_at || dayOfStaff(day, staff).left_at)"
                                    class="font-semibold"
                                >
                                    {{ $filters.format(dayOfStaff(day, staff).start_at, 'HH:mm') }}
                                    -
                                    {{ $filters.format(dayOfStaff(day, staff).left_at, 'HH:mm') }}
                                </div>
                                <div v-else>
                                    {{ $filters.format(dayOfStaff(day, staff).should_start_at, 'HH:mm') }}
                                    -
                                    {{ $filters.format(dayOfStaff(day, staff).should_left_at, 'HH:mm') }}
                                </div>
                                <div
                                    v-if="dayOfStaff(day, staff).classroom"
                                    class="text-xs opacity-70"
                                >
                                    {{ dayOfStaff(day, staff).classroom.name }}
                                </div>
                            </div>
                            <div
                                v-else
                                class="absolute inset-0 cursor-pointer hover:bg-blue-100"
                                @click="create(day, staff)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <table
                v-if="viewMode === 'day'"
                class="text-xs table-fixed bordered"
                aria-labelledby="Vue par jour des enfants"
            >
                <thead>
                    <tr>
                        <th
                            scope="col"
                            class="w-48 py-2"
                        />
                        <th
                            v-for="hour in hours"
                            :key="hour"
                            scope="col"
                            colspan="4"
                            class="py-2 text-sm text-gray-900 capitalize bg-slate-300"
                        >
                            {{ hour }}h
                        </th>
                    </tr>
                    <tr>
                        <th
                            scope="col"
                            class="py-2 font-semibold text-center text-gray-800 bg-slate-200"
                        />
                        <td
                            v-for="quarterHour in quarterHours"
                            :key="quarterHour"
                            class="w-8 text-xs text-center text-gray-800 bg-slate-200"
                        />
                    </tr>
                </thead>
                <tbody>
                    <template
                        v-for="staff in staffWithAttendances"
                        :key="staff.uuid"
                    >
                        <StaffCalendarAttendanceDay
                            v-if="staff.attendances.length"
                            :key="staff.uuid"
                            :range-from="rangeFrom"
                            :range-to="rangeTo"
                            :staff-attendance="staff.attendances[0]"
                            :staff="staff"
                            @updated="getStaffAttendances"
                        />
                        <tr v-else>
                            <td>
                                <div class="flex items-center ml-1 space-x-2">
                                    <img
                                        class="w-6 h-6 rounded-full shadow"
                                        :src="staff.avatar"
                                        alt="staff avatar"
                                    >
                                    <span class="text-xs truncate">{{ staff.name }}</span>
                                </div>
                            </td>
                            <td
                                :colspan="quarterHours.length"
                                class="relative"
                            >
                                <button
                                    class="absolute inset-0 justify-center hover:bg-blue-200 print:hidden"
                                    @click="create(from.format('YYYY-MM-DD'), staff)"
                                >
                                    +
                                </button>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>

            <div
                v-if="showQuota"
                class="text-right mt-8 text-gray-500 text-sm"
            >
                <div v-if="quota !== null">
                    <span
                        class="badge"
                        :class="{ 'badge-green': quota >0, 'badge-red': quota <= 0 }"
                    >{{ quota }}</span> jours de congé restants
                </div>
                <div v-if="overTime !== null">
                    <span
                        class="badge"
                        :class="{ 'badge-green': overTime >0, 'badge-red': overTime <= 0 }"
                    >{{ overTime }}</span>
                    heures supplémentaire restantes
                </div>
            </div>
        </div>

        <UiModal id="attendance-edit">
            <staff-attendance-edit-form
                v-if="selectedStaffAttendance"
                :staff="selectedStaffAttendance.staff"
                :staff-attendance="selectedStaffAttendance"
                @updated="closeModal"
                @deleted="closeModal"
            />
        </UiModal>
        <UiModal id="attendance-create">
            <staff-attendance-create-form
                v-if="createSelectedDate"
                :staff="createSelectedStaff"
                :date="createSelectedDate"
                @updated="closeModal"
            />
        </UiModal>
    </div>
</template>

<script>
import dayjs from 'dayjs'
import { types } from 'abcreche-constants/src/const/staffs/attendances/types'
import { dateRangeMixin, downloadMixin, loadingMixin } from 'abcreche-ui'
import isoWeek from 'dayjs/plugin/isoWeek'
import classroomOptions from '~~/mixins/classrooms/classroomOptions'
import StaffAttendanceRepository from '~~/repositories/Creche/StaffAttendanceRepository.js'
import DocumentRepository from '~~/repositories/Creche/DocumentRepository.js'
import permissionsMixin from '~~/mixins/permissionsMixin.js'
import StaffRepository from '~~/repositories/Creche/StaffRepository.js'

dayjs.extend(isoWeek)

export default {
    mixins: [dateRangeMixin, permissionsMixin, downloadMixin, loadingMixin, classroomOptions],

    props: {
        staffs: {
            type: Array,
            required: true
        },
        showQuota: {
            type: Boolean,
            default: false
        }
    },

    data () {
        return {
            today: dayjs(),
            from: dayjs().startOf('month'),
            to: dayjs().endOf('month'),
            viewMode: 'month',
            staffAttendances: [],
            filters: {
                classroom_uuids: []
            },
            selectedStaffAttendance: null,
            createSelectedDate: null,
            createSelectedStaff: null,
            quota: null,
            overTime: null
        }
    },

    computed: {
        staffWithAttendances () {
            return this.staffs
                .filter((staff) => {
                    if (this.accessStaff) {
                        return true
                    }
                    return staff?.user?.uuid === this.$user.uuid
                })
                .filter((staff) => {
                    if (this.filters.classroom_uuids.length) {
                        return staff.is_working_in_classrooms
                    }
                    return true
                })
                .map((staff) => {
                    staff.attendances = this.staffAttendances
                        .filter(sa => sa.staff?.uuid === staff.uuid)
                        .filter((sa) => {
                            if (this.filters.classroom_uuids.length) {
                                return sa.classroom && this.filters.classroom_uuids.includes(sa.classroom.uuid)
                            }
                            return true
                        })
                        .map((sa) => {
                            let total = Math.abs(dayjs(sa.should_start_at).diff(sa.should_left_at, 'hours', true))
                            if (sa.pause_start_at) {
                                total -= Math.abs(dayjs(sa.date + ' ' + sa.pause_start_at).diff(sa.date + ' ' + sa.pause_stop_at, 'hours', true))
                            }
                            sa.duration = total

                            let realTotal = Math.abs(dayjs(sa.start_at).diff(sa.left_at, 'hours', true))
                            if (sa.pause_start_at) {
                                realTotal -= Math.abs(dayjs(sa.date + ' ' + sa.pause_start_at).diff(sa.date + ' ' + sa.pause_stop_at, 'hours', true))
                            }
                            sa.real_duration = realTotal

                            return sa
                        })
                    staff.total_hours = Math.round(staff.attendances.reduce((sum, a) => sum + a.duration, 0) * 100) / 100
                    staff.real_total_hours = Math.round(staff.attendances.filter(a => !!a.real_duration).reduce((sum, a) => sum + a.real_duration, 0) * 100) / 100
                    return staff
                })
        },
        days () {
            return this.generateDateRange(this.from, this.to, 1, 'day')
        },
        rangeFrom () {
            return dayjs().hour(6).minute(0)
        },
        rangeTo () {
            return dayjs().hour(19).minute(59)
        },
        hours () {
            return (this.viewMode === 'day')
                ? this.generateDateRange(
                        dayjs().hour(6).minute(0),
                        dayjs().hour(19).minute(59),
                        1,
                        'hour',
                        'H'
                    )
                : []
        },
        quarterHours () {
            return (this.viewMode === 'day')
                ? this.generateDateRange(
                        dayjs().hour(6).minute(0),
                        dayjs().hour(19).minute(59),
                        15,
                        'minutes',
                        'HH:mm'
                    )
                : []
        }
    },

    created () {
        this.getStaffAttendances()
        this.fetchClassrooms()
            .catch(error => this.pageError(error))

        if (this.showQuota) {
            this.getQuota()
        }
    },

    methods: {
        canSeeRealHoursForStaff (staff) {
            return this.accessStaff || staff?.user?.uuid === this.$user?.uuid
        },

        goTo (url) {
            if (this.accessStaff) {
                this.$router.push(url)
            }
        },
        isWeekend (day) {
            return [6, 7].includes(dayjs(day).isoWeekday())
        },
        isAm (attendance) {
            return dayjs(attendance.should_start_at).hour() < 11 && !this.isPm(attendance)
        },
        isPm (attendance) {
            return dayjs(attendance.should_left_at).hour() >= 15
        },

        dayOfStaff (day, staff) {
            return staff.attendances
                .find(sa => sa.date === day)
        },

        edit (staffAttendance) {
            if (!this.accessStaff) {
                return
            }
            this.$modal.show('attendance-edit')
            this.selectedStaffAttendance = staffAttendance
        },

        create (day, staff) {
            if (!this.accessStaff) {
                return
            }
            this.$modal.show('attendance-create')

            this.createSelectedDate = day
            this.createSelectedStaff = staff
        },

        closeModal () {
            this.$modal.closeAll()
            this.getStaffAttendances()
        },

        changeDates ({ from, to }) {
            this.from = dayjs(from)
            this.to = dayjs(to)
            this.getStaffAttendances()
        },

        getStaffAttendances () {
            StaffAttendanceRepository.getAll({
                from: this.from.format('YYYY-MM-DD'),
                to: this.to.format('YYYY-MM-DD')
            })
                .then((response) => {
                    this.staffAttendances = response.data
                        .filter((attendance) => {
                            if (this.filters.classroom) {
                                return attendance.classroom ? attendance.classroom.uuid === this.filters.classroom : false
                            }
                            return true
                        })
                        .map((a) => {
                            a.color = types().find(t => t.value === parseInt(a.type))
                            if (a.start_at && a.left_at) {
                                a.color.color = 'attendance-presence'
                            }
                            return a
                        })
                })
        },

        setWeekMode () {
            this.viewMode = 'week'
            this.from = dayjs().startOf('week')
            this.to = dayjs().endOf('week').subtract(2, 'days') // dont need samedi et dimanche
            this.getStaffAttendances()
        },
        setMonthMode () {
            this.viewMode = 'month'
            this.from = dayjs().startOf('month')
            this.to = dayjs().endOf('month')
            this.getStaffAttendances()
        },
        setDayMode () {
            this.viewMode = 'day'
            this.from = dayjs().startOf('day')
            this.to = dayjs().endOf('day')
            this.getStaffAttendances()
        },

        downloadExcel () {
            this.load('excel')
            const data = this.staffAttendances.map(s => ({
                'uuid': s.uuid,
                'Nom': s.staff?.name,
                'Section': s.classroom?.name || '',
                'date': s.date,
                'Arrivée réelle': s.start_at,
                'Départ réel': s.left_at,
                'Arrivée théorique': s.should_start_at,
                'Départ théorique': s.should_left_at,
                'Début Pause': s.pause_start_at,
                'Fin Pause': s.pause_left_at,
                'note': s.note,
                'type': s.type,
                'type_label': s.type_label,
                'extra_type': s.extra_type,
                'Durée': s.duration,
                'Durée réelle': s.real_duration
            }))
            DocumentRepository.dataToExcel(data, 'planning-personnel')
                .then((response) => {
                    this.done(null, 'excel')
                    this.downloadFileFromResponse(response)
                })
        },

        getQuota () {
            StaffRepository.quota(this.staffs[0]?.uuid, { year: dayjs(this.from).format('YYYY') })
                .then((response) => {
                    this.quota = response.quota
                    this.overTime = response.overtime_hours
                })
        }
    }
}
</script>
